import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage, LANGUAGES } from '../../context/LanguageContext';
import { translations, LINKS, NIMNY_AVATAR, getNimnyAppUrl } from '../../data/translations';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopLangOpen, setIsDesktopLangOpen] = useState(false);
  const [isMobileLangOpen, setIsMobileLangOpen] = useState(false);
  const desktopLangRef = useRef(null);
  const mobileLangRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close desktop language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (desktopLangRef.current && !desktopLangRef.current.contains(event.target)) {
        setIsDesktopLangOpen(false);
      }
      if (mobileLangRef.current && !mobileLangRef.current.contains(event.target)) {
        setIsMobileLangOpen(false);
      }
    };

    // Use mousedown instead of click to handle before the click completes
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
    setIsMobileLangOpen(false);
  };

  const handleDesktopLanguageChange = (langCode) => {
    setLanguage(langCode);
    setIsDesktopLangOpen(false);
  };

  const handleMobileLanguageChange = (langCode) => {
    setLanguage(langCode);
    setIsMobileLangOpen(false);
  };

  const navLinks = [
    { label: t(translations.nav.meetNimny), href: '#who-is-nimny' },
    { label: t(translations.nav.stories), href: '#stories' },
    { label: t(translations.nav.updates), href: '#signup' }
  ];

  // Track button clicks for analytics
  const trackClick = (buttonName) => {
    if (window.plausible) {
      window.plausible('Click', { props: { button: buttonName } });
    }
  };

  // Shared button styles for consistency
  const buttonBaseClass = "font-sans rounded-xl px-5 py-2 h-10 text-sm transition-all duration-300";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
        isScrolled
          ? 'bg-[#1a1a2e]/80 backdrop-blur-lg shadow-lg py-3'
          : 'bg-[#0f0f1a]/30 backdrop-blur-sm py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo with Avatar */}
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 group"
            data-testid="header-logo"
          >
            {/* Nimny Avatar - proper circular mask with no white edges */}
            <div 
              className={`w-10 h-10 rounded-full overflow-hidden border-2 transition-all duration-300 ${
                isScrolled
                  ? 'border-amber-300/60'
                  : 'border-amber-200/30'
              }`}
              style={{ backgroundColor: '#d4a574' }}
            >
              <img 
                src={NIMNY_AVATAR}
                alt="Nimny"
                className="w-full h-full object-cover"
                style={{ 
                  transform: 'scale(1.2)',
                  objectPosition: 'center 35%'
                }}
              />
            </div>
            <span className={`font-sans text-xl transition-colors ${
              isScrolled ? 'text-[#f5ebe0]/90' : 'text-amber-100/80'
            }`}>
              Nimny
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className={`font-sans text-sm px-3 py-1.5 rounded-[20px] border border-[#F4E6C8]/25 transition-all duration-[250ms] hover:bg-[#F4E6C8]/10 hover:border-[#F4E6C8]/40 ${
                  isScrolled ? 'text-amber-200/80' : 'text-amber-100/80'
                }`}
              >
                {link.label}
              </button>
            ))}
            
            {/* Read Online - Text link style for header (secondary nav) */}
            <a
              href={getNimnyAppUrl(language)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick('Read Online - Header')}
              data-testid="header-read-online"
              className={`font-sans text-sm px-3 py-1.5 rounded-[20px] border border-[#F4E6C8]/25 transition-all duration-[250ms] hover:bg-[#F4E6C8]/10 hover:border-[#F4E6C8]/40 ${
                isScrolled ? 'text-amber-200/80' : 'text-amber-100/80'
              }`}
            >
              {t(translations.nav.readOnline)}
            </a>

            {/* Read on Amazon - Text link style for header (secondary nav) */}
            <a
              href={LINKS.amazon}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick('Read on Amazon - Header')}
              data-testid="header-read-amazon"
              className={`font-sans text-sm px-3 py-1.5 rounded-[20px] border border-[#F4E6C8]/25 transition-all duration-[250ms] hover:bg-[#F4E6C8]/10 hover:border-[#F4E6C8]/40 ${
                isScrolled ? 'text-amber-200/80' : 'text-amber-100/80'
              }`}
            >
              {t(translations.nav.readOnAmazon)}
            </a>

            {/* Language Switcher - Desktop (SEPARATE from mobile) */}
            <div className="relative" ref={desktopLangRef}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDesktopLangOpen(!isDesktopLangOpen);
                }}
                className={`flex items-center gap-1 font-sans text-sm px-3 py-1.5 rounded-[20px] border border-[#F4E6C8]/25 transition-all duration-[250ms] hover:bg-[#F4E6C8]/10 hover:border-[#F4E6C8]/40 cursor-pointer ${
                  isScrolled ? 'text-amber-200/80' : 'text-amber-100/80'
                }`}
                data-testid="language-switcher-desktop"
                type="button"
              >
                <Globe className="w-4 h-4" />
                <span>{LANGUAGES[language].name}</span>
              </button>
              
              {isDesktopLangOpen && (
                <div className="absolute right-0 mt-2 py-2 w-36 bg-[#1a1a2e] border border-amber-200/20 rounded-xl shadow-xl z-[200]">
                  {Object.values(LANGUAGES).map((lang) => (
                    <button
                      key={lang.code}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDesktopLanguageChange(lang.code);
                      }}
                      className={`w-full px-4 py-3 text-left font-sans text-sm transition-colors hover:bg-amber-200/10 cursor-pointer ${
                        language === lang.code ? 'text-amber-300 bg-amber-200/5' : 'text-amber-200/70'
                      }`}
                      type="button"
                    >
                      {lang.name} - {lang.fullName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-3 md:hidden">
            {/* Mobile Language Switcher (SEPARATE from desktop) */}
            <div className="relative" ref={mobileLangRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMobileLangOpen(!isMobileLangOpen);
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center gap-1 text-amber-200 text-sm p-2"
                aria-label="Change language"
                data-testid="language-switcher-mobile"
              >
                <Globe className="w-4 h-4" />
                <span>{LANGUAGES[language].name}</span>
              </button>
              
              {/* Mobile Language Dropdown */}
              {isMobileLangOpen && (
                <div className="absolute right-0 mt-2 py-2 w-36 bg-[#1a1a2e] border border-amber-200/20 rounded-xl shadow-xl z-[200]">
                  {Object.values(LANGUAGES).map((lang) => (
                    <button
                      key={lang.code}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMobileLanguageChange(lang.code);
                      }}
                      className={`w-full px-4 py-3 text-left font-sans text-sm transition-colors hover:bg-amber-200/10 ${
                        language === lang.code ? 'text-amber-300 bg-amber-200/5' : 'text-amber-200/70'
                      }`}
                    >
                      {lang.name} - {lang.fullName}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Hamburger / Close Button */}
            <button
              onClick={() => {
                setIsMobileMenuOpen(!isMobileMenuOpen);
                setIsMobileLangOpen(false);
              }}
              className="p-2 text-amber-200 z-[60]"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-300 overflow-hidden ${
            isMobileMenuOpen ? 'max-h-[500px] mt-4 opacity-100' : 'max-h-0 opacity-0'
          }`}
          style={{ backgroundColor: isScrolled ? 'transparent' : 'rgba(26, 26, 46, 0.95)' }}
        >
          <nav className="flex flex-col gap-3 py-4 border-t border-amber-200/20 bg-[#1a1a2e]/95 rounded-b-xl px-2">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="font-sans text-amber-200/80 hover:text-amber-300 text-left py-2.5 px-4 rounded-[20px] border border-[#F4E6C8]/20 active:bg-[#F4E6C8]/15 hover:bg-[#F4E6C8]/8 transition-all duration-[250ms] text-base"
              >
                {link.label}
              </button>
            ))}
            
            {/* Mobile Read Online Button - Unified warm gold */}
            <a
              href={getNimnyAppUrl(language)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackClick('Read Online - Header Mobile');
                setIsMobileMenuOpen(false);
              }}
              className="mt-2"
            >
              <Button 
                className="w-full bg-[#b8956b] hover:bg-[#a8855b] text-white font-sans rounded-xl py-3 shadow-md text-base"
              >
                {t(translations.nav.readOnline)}
              </Button>
            </a>
            
            {/* Mobile Read on Amazon Button - Outline style */}
            <a
              href={LINKS.amazon}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackClick('Read on Amazon - Header Mobile');
                setIsMobileMenuOpen(false);
              }}
            >
              <Button 
                className="w-full bg-transparent border border-[#b8956b]/50 hover:bg-[#b8956b]/10 text-[#d4b896] font-sans rounded-xl py-3 text-base"
              >
                {t(translations.nav.readOnAmazon)}
              </Button>
            </a>
          </nav>
        </div>
      </div>

      {/* Overlay to close menu when tapping outside */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/20 z-[-1]"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
};

export default Header;
